/*
 * ------------------------------------------------------------------
 * Theme Sauce - buzzwoo E-Com Kitchen
 * bw-iv-payment-slider.js | Auto-Slide fuer den Zahlungsarten-Block
 * ------------------------------------------------------------------
 * Custom Element <bw-pay-slider> um die Icon-Liste des PDP-Blocks
 * „Zahlungsarten" (bw-iv-payment-block.liquid). Das Wischen selbst
 * macht reines CSS (overflow-x + scroll-snap in bw-iv-payment-block.css,
 * Klasse .bw-iv-paybox--m-slider); dieses Script blaettert nur
 * selbsttaetig weiter.
 *
 *   data-bw-autoplay="true"   aktiviert das Weiterblaettern
 *   data-bw-speed="3"         Sekunden pro Schritt
 *
 * Bewusst KEIN Ignite <slider-component>: der bringt Grid-Klassen und
 * die komplette Prev/Next/Counter-Steuerleiste mit, an der seine
 * Paging-Logik haengt (global.js initPages). Fuer eine Reihe kleiner
 * Logos in der Buybox ist das zu viel DOM fuer zu wenig Nutzen.
 *
 * Pausen-Logik 1:1 aus bw-iv-usp-autoscroll.js: Hover nur bei echtem
 * Zeigergeraet, Tastaturfokus, Beruehrung, Tab im Hintergrund,
 * prefers-reduced-motion, Blockauswahl im Theme-Editor. Erster Schritt
 * nach FIRST_STEP_DELAY (Hinweis „hier laesst sich wischen"), danach im
 * eingestellten Takt. Am Ende zurueck an den Anfang.
 *
 * Laeuft nur, wenn die Liste WIRKLICH ueberlaeuft und der mobile
 * Breakpoint aktiv ist -- auf Desktop ist der Block eine normale Reihe
 * und das Element bleibt passiv.
 *
 * Sichtbarer Pause/Play-Schalter (.bw-iv-paybox__pause, WCAG 2.2.2): eine
 * dauerhafte Sperre `user`, die keine der automatischen Pausen aufhebt.
 * Fokus- und Touch-Sperren haengen am SCROLLER, nicht am Element -- sonst
 * bliebe der Slider nach dem Tippen auf „Play" durch den Fokus auf dem
 * Schalter selbst gesperrt.
 *
 * disconnectedCallback raeumt Timer + Listener ab: die Section-
 * Rendering-API tauscht den Block im Editor komplett aus.
 * ------------------------------------------------------------------
 */
(() => {
  'use strict';

  if (customElements.get('bw-pay-slider')) return;

  const FIRST_STEP_DELAY = 1000;
  const MOBILE = '(max-width: 749.98px)';

  class BwPaySlider extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('.bw-iv-paybox__list');
      if (!this.scroller) return;
      this.pauseButton = this.querySelector('.bw-iv-paybox__pause');

      const seconds = parseFloat(this.dataset.bwSpeed);
      this.delay = (Number.isFinite(seconds) && seconds > 0 ? seconds : 3) * 1000;
      this.autoplay = this.dataset.bwAutoplay === 'true';

      this.timer = null;
      this.inViewport = false;
      this.firstStepDone = false;
      this.blocked = new Set();
      this.abort = new AbortController();

      this.mobile = window.matchMedia(MOBILE);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.tick = this.tick.bind(this);
      this.sync = this.sync.bind(this);

      if (!this.autoplay) return;

      this.bindEvents();
      this.observe();
      this.sync();
    }

    disconnectedCallback() {
      if (this.timer !== null) clearTimeout(this.timer);
      this.timer = null;
      if (this.observer) this.observer.disconnect();
      if (this.abort) this.abort.abort();
    }

    block(reason) {
      this.blocked.add(reason);
      this.sync();
    }

    unblock(reason) {
      this.blocked.delete(reason);
      this.sync();
    }

    sync() {
      const mayRun =
        this.inViewport &&
        this.blocked.size === 0 &&
        this.mobile.matches &&
        !this.reducedMotion.matches &&
        !document.hidden;

      if (mayRun) {
        if (this.timer === null) {
          const wait = this.firstStepDone ? this.delay : FIRST_STEP_DELAY;
          this.timer = setTimeout(this.tick, wait);
        }
      } else if (this.timer !== null) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    bindEvents() {
      const { signal } = this.abort;
      const opts = { signal };
      const passive = { signal, passive: true };

      if (window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', () => this.block('hover'), opts);
        this.addEventListener('mouseleave', () => this.unblock('hover'), opts);
      }
      const el = this.scroller;
      el.addEventListener('focusin', () => this.block('focus'), opts);
      el.addEventListener('focusout', () => this.unblock('focus'), opts);
      el.addEventListener('touchstart', () => this.block('touch'), passive);
      el.addEventListener('touchend', () => this.unblock('touch'), passive);
      el.addEventListener('touchcancel', () => this.unblock('touch'), passive);

      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', () => this.togglePaused(), opts);
      }

      document.addEventListener('visibilitychange', this.sync, opts);
      this.reducedMotion.addEventListener('change', this.sync, opts);
      this.mobile.addEventListener('change', this.sync, opts);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (this.contains(event.target)) this.block('editor');
        }, opts);
        document.addEventListener('shopify:block:deselect', (event) => {
          if (this.contains(event.target)) this.unblock('editor');
        }, opts);
      }
    }

    togglePaused() {
      const paused = this.pauseButton.getAttribute('aria-pressed') !== 'true';
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      const label = paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause;
      if (label) this.pauseButton.setAttribute('aria-label', label);
      if (paused) {
        this.block('user');
      } else {
        this.unblock('user');
      }
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.inViewport = true;
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) this.inViewport = entry.isIntersecting;
          this.sync();
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this);
    }

    tick() {
      this.timer = null;
      this.firstStepDone = true;

      const el = this.scroller;
      if (el.scrollWidth > el.clientWidth + 1) {
        const items = el.querySelectorAll('.bw-iv-paybox__item, .bw-iv-paybox__chip');
        const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : el.clientWidth;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

        if (atEnd) {
          el.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          el.scrollBy({ left: step, behavior: 'smooth' });
        }
      }

      this.sync();
    }
  }

  customElements.define('bw-pay-slider', BwPaySlider);
})();
